import { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useRouter } from 'next/router'
import { useLidoStakingStats, useLidoUserData } from './hooks'
import PrimaryButton from '~/components/Button/PrimaryButton'
import Tooltip from '~/components/Tooltip'
import {
  StyledStakedInfo,
  StyledStakedLabel,
  StyledStakedAmount,
  StyledActionButtons,
  StyledActionButton,
  StyledStakedLabelWrapper,
  StyledPipe,
} from './styled'
import {
  StyledProjectCard,
  StyledProjectHeader,
  StyledProjectTitleWrapper,
  StyledProjectTitle,
  StyledProjectStats,
  StyledStatItem,
  StyledStatLabel,
  StyledStatValue,
  StyledBrandLabel,
  StyledMyRewardSection,
  StyledMyRewardHeader,
  StyledMyRewardTitle,
  StyledMyRewardAmount,
  StyledMyRewardValue,
} from '../styled'
import { WithdrawToken } from './Withdrawal/types'
import ImgLidoBrand from '~/assets/images/lido-brand.png'
import IconTokenETH from '~/assets/images/token-eth-with-border.svg'
import IconQuestionMark from '~/assets/images/question-mark-gray.svg'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { useIsDisableTrade } from '~/state/dex/hooks'
import { useGA } from '~/hooks'

const LidoCard = () => {
  const { t } = useTranslation()
  const { trackClick } = useGA()
  const router = useRouter()
  const { apr: lidoApr, totalStaked, stakers } = useLidoStakingStats()
  const {
    isStETHBalanceZero,
    isWstETHBalanceZero,
    stETHBalanceFormatted,
    wstETHBalanceFormatted,
    ethRewards,
    currencyRewards,
    isConnected,
  } = useLidoUserData()

  const isDisableTrade = useIsDisableTrade()
  const toggleIpBlockedModal = useModalToggle(ApplicationModal.IP_BLOCKED)

  const rewardsLink = 'https://stake.lido.fi/rewards'

  const handleRewardsLinkClick = () => {
    trackClick('webtl_stk_myreward', {
      url: rewardsLink,
    })
  }

  const handleStakeClick = useCallback(() => {
    trackClick('webtl_stk')
    if (isDisableTrade) {
      toggleIpBlockedModal()
      return
    }

    router.push('/staking/lido/stake?from=homepage')
  }, [router, isDisableTrade])

  const handleWrapClick = useCallback(() => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
      return
    }

    router.push('/staking/lido/wrap?tab=wrap&from=stETH-wrap')
  }, [router, isDisableTrade])

  const handleUnwrapClick = useCallback(() => {
    if (isDisableTrade) {
      toggleIpBlockedModal()
      return
    }

    router.push('/staking/lido/wrap?tab=unwrap&from=wstETH-unwrap')
  }, [router, isDisableTrade])

  const handleWithdrawClick = useCallback(
    (token: WithdrawToken) => {
      if (isDisableTrade) {
        toggleIpBlockedModal()
        return
      }

      const from = token === 'stETH' ? 'stETH-withdraw' : 'wstETH-withdraw'
      router.push(`/staking/lido/withdrawal?token=${token}&from=${from}`)
    },
    [router, isDisableTrade],
  )

  return (
    <StyledProjectCard>
      <StyledProjectHeader>
        <StyledProjectTitleWrapper>
          <img src={IconTokenETH} width={28} height={28} alt="ETH icon" />
          <StyledProjectTitle>{t('lido_page_title')}</StyledProjectTitle>
        </StyledProjectTitleWrapper>
        <StyledBrandLabel>
          <span>By</span>
          <img
            src={ImgLidoBrand.src}
            alt="LIDO brand"
            width={65.97}
            height={15.71}
          />
        </StyledBrandLabel>
      </StyledProjectHeader>

      <StyledProjectStats>
        <StyledStatItem>
          <StyledStatLabel style={{ cursor: 'pointer' }}>
            <Tooltip title={t('lido_apr_tip')} trigger="hover" placement="top">
              APR
            </Tooltip>
          </StyledStatLabel>
          <StyledStatValue color="primary">{lidoApr}</StyledStatValue>
        </StyledStatItem>
        <StyledStatItem style={{ alignItems: 'center' }}>
          <StyledStatLabel>{t('lido_total_staked')}</StyledStatLabel>
          <StyledStatValue>{totalStaked}</StyledStatValue>
        </StyledStatItem>
        <StyledStatItem style={{ alignItems: 'flex-end' }}>
          <StyledStatLabel>{t('lido_stakers')}</StyledStatLabel>
          <StyledStatValue>{stakers}</StyledStatValue>
        </StyledStatItem>
      </StyledProjectStats>

      <PrimaryButton text={t('stake')} handleClick={handleStakeClick} />

      {isConnected && ethRewards !== '0' && (
        <StyledMyRewardSection>
          <StyledMyRewardHeader>
            <StyledMyRewardTitle>{t('my_reward')}</StyledMyRewardTitle>
            <Tooltip
              title={
                <Trans i18nKey="my_reward_tip">
                  若需要查看奖励记录明细，请前往
                  <a
                    href={rewardsLink}
                    onClick={handleRewardsLinkClick}
                    target="_blank"
                  >
                    Lido 官网
                  </a>
                  查看。
                </Trans>
              }
              trigger="hover"
              placement="top"
              mobilePlacement="top"
              img={IconQuestionMark}
              iconClassName="tooltip-wrap-new"
            />
          </StyledMyRewardHeader>

          <StyledMyRewardAmount>{ethRewards} stETH</StyledMyRewardAmount>
          <StyledMyRewardValue>$ {currencyRewards}</StyledMyRewardValue>

          <StyledStakedInfo>
            <StyledStakedLabelWrapper>
              <StyledPipe />
              <div>
                <StyledStakedLabel>{t('staked_steth')}</StyledStakedLabel>
                <StyledStakedAmount>{stETHBalanceFormatted}</StyledStakedAmount>
              </div>
            </StyledStakedLabelWrapper>
            <StyledActionButtons>
              <StyledActionButton
                variant="primary"
                onClick={handleWrapClick}
                disabled={isStETHBalanceZero}
              >
                {t('wrap')}
              </StyledActionButton>
              <StyledActionButton
                variant="secondary"
                onClick={() => handleWithdrawClick('stETH')}
                disabled={isStETHBalanceZero}
              >
                {t('withdraw')}
              </StyledActionButton>
            </StyledActionButtons>
          </StyledStakedInfo>

          {!isWstETHBalanceZero && (
            <StyledStakedInfo>
              <StyledStakedLabelWrapper>
                <StyledPipe />
                <div>
                  <StyledStakedLabel>{t('wsteth_balance')}</StyledStakedLabel>
                  <StyledStakedAmount>
                    {wstETHBalanceFormatted}
                  </StyledStakedAmount>
                </div>
              </StyledStakedLabelWrapper>
              <StyledActionButtons>
                <StyledActionButton
                  variant="primary"
                  onClick={handleUnwrapClick}
                >
                  {t('unwrap')}
                </StyledActionButton>
                <StyledActionButton
                  variant="secondary"
                  onClick={() => handleWithdrawClick('wstETH')}
                >
                  {t('withdraw')}
                </StyledActionButton>
              </StyledActionButtons>
            </StyledStakedInfo>
          )}
        </StyledMyRewardSection>
      )}
    </StyledProjectCard>
  )
}

export default LidoCard
